import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'

import { actionQueries } from '@/features/actions/queries'
import { AutomationSettingsFormSkeleton } from '@/features/air/automation-settings/components/skeletons'
import { automationQueries } from '@/features/air/automation-settings/queries'
import { policyQueries } from '@/features/air/policy-management/queries'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { Section, SectionTitle } from '@/features/app-shell/section'
import { playbookQueries } from '@/features/playbooks/queries'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import BackLink from '@/components/common/back-link'
import { ErrorBoundary } from '@/components/common/error-boundary'

const AutomationSettingsForm = lazy(
	() => import('@/features/air/automation-settings/components/form'),
)

export const Route = createFileRoute(
	'/_dashboard/air/automation-settings/$mapping_id',
)({
	beforeLoad: () => roleAuthGuard(ROLES.Analyst),

	loader: async ({ context: { queryClient }, params }) => {
		const tenant_id = getCurrentTenant()

		if (tenant_id) {
			// Prefetch list of actions
			queryClient.prefetchQuery(actionQueries.list(tenant_id))

			// Prefetch list of playbooks
			queryClient.prefetchQuery(playbookQueries.list(tenant_id))

			const data = await queryClient.ensureQueryData({
				...automationQueries.automation_details(tenant_id, params.mapping_id),
				revalidateIfStale: true,
			})

			// Prefetch policy details for the mapped policy
			const automation = data.responses.find(
				(r) => r.mapping_id === decodeURIComponent(params.mapping_id),
			)
			if (automation?.mapped_id) {
				queryClient.prefetchQuery(
					policyQueries.policy_details(tenant_id, automation.mapped_id),
				)
			}

			return data
		}
	},

	head: ({ loaderData }) => ({
		meta: [
			{
				title: `Edit: ${loaderData?.responses[0].response_name} | Air | BitLyft AIR®`,
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: RoutePendingComponent,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	const { mapping_id } = Route.useParams()
	const data = Route.useLoaderData()

	if (!data) {
		return <RoutePendingComponent />
	}

	return (
		<Section>
			<div className="flex items-start justify-start gap-4">
				<BackLink to="/air/automation-settings" />

				<SectionTitle>{data?.responses[0].response_name}</SectionTitle>
			</div>

			<div className="flex w-full flex-col items-start justify-start gap-2">
				<ErrorBoundary
					feature="air-automation"
					section="edit-form"
					entityId={mapping_id}
				>
					<Suspense fallback={<AutomationSettingsFormSkeleton />}>
						<AutomationSettingsForm mapping_id={mapping_id} />
					</Suspense>
				</ErrorBoundary>
			</div>
		</Section>
	)
}

function RoutePendingComponent() {
	return (
		<Section>
			<div className="flex w-full flex-col items-start justify-start gap-2">
				<AutomationSettingsFormSkeleton />
			</div>
		</Section>
	)
}
